import { environmentHttpClient as httpClient } from "client/Api/httpClient";
import { AvailableAppsViewModel, SpaceKind } from "client/Api/Workspace/workspace";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { AppColorViewModel, AppType } from "client/Api/Apps/common";
import { MaintenanceWindowAppModel } from "../GlobalSearch/globalSearch";

export enum GlobalNavigationItemViewModelKind {
	Space = 0,
	GenericApp = 1,
	Group = 3
}

export type AppGlobalNavigationItemDataModel = {
	kind: GlobalNavigationItemViewModelKind.GenericApp;
	installedAppId: number;
	appType: AppType;
	conditionalAccess: OptionalModel<NavigationItemConditionalAccessRestrictionModel>;
	maintenanceWindow: OptionalModel<MaintenanceWindowAppModel>;
};

export type NavigationItemConditionalAccessRestrictionModel = {
	reason: string;
};

export type SpaceUserInfo = {
	canEdit: boolean;
	canDelete: boolean;
};

export type SpaceGlobalNavigationItemDataModel = {
	kind: GlobalNavigationItemViewModelKind.Space;
	spaceKind: SpaceKind;
	spaceId: number;
	userInfo: SpaceUserInfo;
};

export type GroupGlobalNavigationItemDataModel = {
	kind: GlobalNavigationItemViewModelKind.Group;
	children: readonly GlobalNavigationItemModel[];
};

export type IGlobalNavigationItemDataModel =
	| AppGlobalNavigationItemDataModel
	| SpaceGlobalNavigationItemDataModel
	| GroupGlobalNavigationItemDataModel;

export type GlobalNavigationItemModel = {
	id: number;
	name: string;
	itemData: IGlobalNavigationItemDataModel;
};

export type GlobalNavigationPermissionsModel = {
	canManageSharedSpaces: boolean;
	canChangeGlobalNavigationOrder: boolean;
	canManageSpacePermissions: boolean;
	canManageGlobalNavigation: boolean;
};

export type GlobalNavigationViewModel = {
	items: GlobalNavigationItemModel[];
	permissions: GlobalNavigationPermissionsModel;
};

export enum GlobalNavigationItemKind {
	Space = 1,
	InstalledApp = 2,
	Group = 3
}

export type AppGlobalNavigationItemDataSaveModel = {
	installedAppId: number;
};

export type SpaceGlobalNavigationItemDataSaveModel = {
	spaceId: number;
};

export type GroupGlobalNavigationItemDataSaveModel = {
	children: readonly GlobalNavigationItemSaveModel[];
	name: string;
};

export type AppGlobalNavigationItemSaveModel = {
	id?: number | null;
	kind: GlobalNavigationItemKind.InstalledApp;
	appData: AppGlobalNavigationItemDataSaveModel;
};

export type SpaceGlobalNavigationItemSaveModel = {
	id?: number | null;
	kind: GlobalNavigationItemKind.Space;
	spaceData: SpaceGlobalNavigationItemDataSaveModel;
};

export type GroupGlobalNavigationItemSaveModel = {
	id?: number | null;
	kind: GlobalNavigationItemKind.Group;
	groupData: GroupGlobalNavigationItemDataSaveModel;
};

export type GlobalNavigationItemSaveModel =
	| AppGlobalNavigationItemSaveModel
	| GroupGlobalNavigationItemSaveModel
	| SpaceGlobalNavigationItemSaveModel;

export type AddGlobalNavigationItemsSaveModel = {
	newItems: readonly GlobalNavigationItemSaveModel[];
};

export type AppGlobalNavigationItemDataEditModel = {
	kind: GlobalNavigationItemKind.InstalledApp;
	installedAppId: number;
	appType: AppType;
	image: AppImageDisplayInfo;
	color: AppColorViewModel;
};

export type SpaceGlobalNavigationItemDataEditModel = {
	kind: GlobalNavigationItemKind.Space;
	spaceKind: SpaceKind;
	spaceId: number;
	userInfo: SpaceUserInfo;
};

export type GroupGlobalNavigationItemDataEditModel = {
	kind: GlobalNavigationItemKind.Group;
	children: GlobalNavigationItemEditModel[];
};

export type IGlobalNavigationItemDataEditModel =
	| AppGlobalNavigationItemDataEditModel
	| SpaceGlobalNavigationItemDataEditModel
	| GroupGlobalNavigationItemDataEditModel;

export type GlobalNavigationItemEditModel = {
	id: number;
	name: string;
	itemData: IGlobalNavigationItemDataEditModel;
};

export type GlobalNavigationEditModel = {
	items: GlobalNavigationItemEditModel[];
	permissions: GlobalNavigationPermissionsModel;
};

export type GlobalNavigationSaveModel = {
	items: GlobalNavigationItemSaveModel[];
};

export interface GlobalNavigationSaveResult {
	addedNavigationItems: GlobalNavigationItemModel[];
}

export function getNavigation(signal?: AbortSignal): Promise<GlobalNavigationViewModel> {
	return httpClient.get<GlobalNavigationViewModel>(
		"TitleBar/Navigation",
		undefined,
		{ signal }
	);
}

export function getNavigationForEdit(signal?: AbortSignal): Promise<GlobalNavigationEditModel> {
	return httpClient.get<GlobalNavigationEditModel>(
		"TitleBar/Navigation/Edit",
		undefined,
		{ signal }
	);
}

export function getAppsAvailableToPin(
	alreadyPinnedAppIdsThatShouldAlsoBeIncluded?: readonly number[],
	signal?: AbortSignal
): Promise<AvailableAppsViewModel> {
	return httpClient.post<AvailableAppsViewModel>(
		"TitleBar/Navigation/GetAppsAvailableToPin",
		{ alreadyPinnedAppIdsThatShouldAlsoBeIncluded },
		{ signal }
	);
}

export function saveNavigation(model: GlobalNavigationSaveModel, signal?: AbortSignal): Promise<GlobalNavigationSaveResult> {
	return httpClient.put<GlobalNavigationSaveResult>(
		"TitleBar/Navigation",
		model,
		{ signal }
	);
}

export function addNavigationItems(
	model: AddGlobalNavigationItemsSaveModel,
	signal?: AbortSignal
): Promise<GlobalNavigationSaveResult> {
	return httpClient.post<GlobalNavigationSaveResult>(
		"TitleBar/Navigation/Items",
		model,
		{ signal }
	);
}
